import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { isEmpty, toNumber } from 'lodash-es';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import ErrorPage from '@/components/errors/ErrorPage';
import InternalServerError from '@/components/errors/InternalServerError';
import SingleColumnTemplate from '@/components/layouts/SingleColumnTemplate';
import UiStackTemplate from '@/components/layouts/UiStackTemplate';
import Select, { type OptionObject } from '@/components/ui/forms/Select';
import PageTitle from '@/components/ui/headers/PageTitle';
import useFetch from '@/hooks/common/useFetch';
import useQueryParams from '@/hooks/common/useQueryParams';
import { type Student } from '@/interfaces/student';
import { AppContext } from '@/providers/ContextProvider';

import styles from './AbsencesIndexPage.module.scss';
import AbsencesIndexFooter from './index/AbsencesIndexFooter';
import AbsencesIndexList from './index/AbsencesIndexList';
import AbsencesEmpty from './parts/AbsencesEmpty';
import AbsencesRestricted from './parts/AbsencesRestricted';
import { type ContactType, type Reason } from './types';

interface LatestLog {
  id: number;
  contact_type: ContactType;
  reason: Reason;
  detail_comment: string;
  go_to_school_at: string;
  leave_school_at: string;
  status: string;
  is_created_teacher: boolean;
  confirmation?: {
    confirmed: boolean;
  };
}
interface Absence {
  id: number;
  date: string;
  organization_membership_id: string;
  latest_logs: LatestLog[];
}
export interface AbsencesIndexPayload {
  absences: Absence[];
  pagination: {
    current_page: number;
    limit_value: number;
    total_count: number;
  };
  organization_management?: {
    absence_time_limit?: string;
  };
}

type IndexState =
  | 'ok'
  | 'forbidden'
  | 'restricted'
  | 'notAllowed'
  | 'empty'
  | 'notFound'
  | 'serverError'
  | null;

const AbsencesIndexPage: React.FC = () => {
  const [indexState, setIndexState] = useState<IndexState>(null);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { organizationMembershipId } = useParams();
  const { currentUser } = useContext(AppContext);

  const students = useMemo<Student[]>(
    () => currentUser?.organization_memberships ?? [],
    [currentUser],
  );
  const defaultStudent = students[0];
  const studentSelectOptions = useMemo<OptionObject[]>(
    () =>
      students.map((student) => ({
        name: student.name,
        value: student.id,
      })),
    [students],
  );
  const isAllowedAbsence = useMemo<boolean>(
    () => selectedStudent?.organization_management?.allowed_absence ?? false,
    [selectedStudent],
  );

  const {
    fetch: fetchAbsences,
    isFetching,
    status,
    data: payload,
  } = useFetch<AbsencesIndexPayload>(
    'GET',
    '/api/school_communication/absences',
  );

  const handleFetchAbsences = useCallback(
    ({
      page,
      organizationMembershipId,
    }: {
      page: number;
      organizationMembershipId?: string;
    }) =>
      void fetchAbsences({
        body: {
          page,
          organization_membership_id: organizationMembershipId,
        },
      }),
    [fetchAbsences],
  );

  const query = useQueryParams();
  const pageParam = query.get('page');
  const page = pageParam || '1';

  const handleOnChange = useCallback(
    (newVal: string | null) => {
      let param = '';
      if (pageParam && organizationMembershipId === newVal) {
        param = `/?page=${page}`;
      }

      if (newVal) {
        const path = `/absences/member/${newVal}${param}`;
        const { pathname, search } = location;

        if (path !== `${pathname}${search}`) {
          void navigate(path);
        }
        setSelectedStudent(
          students.find((student) => student.id === newVal) || null,
        );
      }
    },
    [pageParam, organizationMembershipId, page, location, students, navigate],
  );

  useEffect(() => {
    const validStudent = students.find(
      (student) => student.id === organizationMembershipId,
    );

    if (organizationMembershipId && !validStudent) {
      return;
    }

    if (validStudent) {
      setSelectedStudent(validStudent);
    } else {
      setSelectedStudent(defaultStudent);
    }
  }, [students, organizationMembershipId, defaultStudent, setSelectedStudent]);

  useEffect(() => {
    setIndexState(null);

    if (selectedStudent) {
      if (selectedStudent.id !== organizationMembershipId) {
        setSelectedStudent(
          students.find((obj) => obj.id === organizationMembershipId) || null,
        );
        return;
      }

      if (isAllowedAbsence) {
        handleFetchAbsences({
          page: parseInt(page),
          organizationMembershipId: selectedStudent.id,
        });
      }
    }
  }, [
    location,
    selectedStudent,
    students,
    organizationMembershipId,
    page,
    isAllowedAbsence,
    handleFetchAbsences,
  ]);

  useEffect(() => {
    setIndexState(null);
    const statusCode = toNumber(status);
    if (!isFetching) {
      // 存在しないページ番号のパラメータが要求された時
      const totalCount = payload?.pagination.total_count || 0;
      if (status === 200 && totalCount > 0 && isEmpty(payload?.absences)) {
        return setIndexState('notFound');
      }

      if (status === 200 && payload?.pagination.total_count === 0) {
        return setIndexState('empty');
      }

      if (statusCode === 200) {
        return setIndexState('ok');
      }

      if (statusCode === 403) {
        return setIndexState('restricted');
      }
      if (statusCode === 423) {
        return setIndexState('notAllowed');
      }

      if (statusCode >= 400 && statusCode < 500) {
        return setIndexState('notFound');
      }

      if (statusCode >= 500 && statusCode < 600) {
        return setIndexState('serverError');
      }

      const validStudent = students.find(
        (student) => student.id === organizationMembershipId,
      );

      if (organizationMembershipId && !validStudent) {
        return setIndexState('notFound');
      }

      return setIndexState(null);
    }
  }, [
    status,
    isFetching,
    selectedStudent,
    payload?.absences,
    payload?.pagination.total_count,
    students,
    organizationMembershipId,
  ]);

  useEffect(() => {
    if (!organizationMembershipId && defaultStudent) {
      navigate(`/absences/member/${defaultStudent?.id}`, {
        replace: true,
      });
      setSelectedStudent(defaultStudent);
    }
  }, [defaultStudent, navigate, organizationMembershipId]);

  const renderContentSwitch: (
    state: IndexState,
    payload: AbsencesIndexPayload | null,
  ) => React.ReactNode = (state: IndexState) => {
    if (state === 'ok') {
      if (payload && selectedStudent) {
        return (
          <AbsencesIndexList
            organizationMembershipId={selectedStudent.id}
            payload={payload}
          />
        );
      }

      return <AbsencesEmpty />;
    }

    if (state === 'serverError') {
      return <InternalServerError />;
    }

    if (state === 'empty') {
      return <AbsencesEmpty />;
    }

    if (state === 'restricted') {
      return <AbsencesRestricted type="notContracted" />;
    }

    if (state === 'notAllowed') {
      return <AbsencesRestricted type="notAllowed" />;
    }

    return null;
  };

  if (indexState === 'notFound') {
    return <ErrorPage type="not_found" />;
  }
  if (indexState === 'forbidden') {
    return <ErrorPage type="forbidden" />;
  }

  return (
    <SingleColumnTemplate>
      <div className={styles.wrapper}>
        {indexState !== 'serverError' && <PageTitle title="欠席連絡" />}
        <div className={styles.mainWrapper}>
          <div className={styles.mainInner}>
            {indexState !== 'serverError' && !isEmpty(students) && (
              <div
                className={styles.studentSelector}
                data-testid="student_selector"
              >
                <span className={styles.selectLabel}>生徒：</span>
                <div className={styles.selectWrapper}>
                  <Select
                    options={studentSelectOptions}
                    value={selectedStudent?.id}
                    onChange={handleOnChange}
                  />
                </div>
              </div>
            )}
            {isAllowedAbsence ? (
              <UiStackTemplate isLoading={isFetching}>
                {renderContentSwitch(indexState, payload)}
              </UiStackTemplate>
            ) : (
              <AbsencesRestricted type="notAllowed" />
            )}
          </div>
        </div>
        {selectedStudent &&
          isAllowedAbsence &&
          (indexState === 'ok' || indexState === 'empty') &&
          !isFetching && (
            <AbsencesIndexFooter
              studentId={selectedStudent.id}
              organizationManagement={payload?.organization_management}
            />
          )}
      </div>
    </SingleColumnTemplate>
  );
};

export default AbsencesIndexPage;
